import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import withReport from 'components/with-report';
import Spinner from 'components/spinner';
import {
  getReportResult,
  getReportResultCSV,
  reportResultFileName,
  getContractorReport
} from 'api/reports';
import { downloadCSV } from 'utils/file';
import { DownloadSimple, CaretLeft } from 'phosphor-react';
import ReportResult from 'components/shared/report-result';
import DownloadStartedModal from 'components/download-started-modal';
import { Report } from 'graphql-api';
import { navigateAndStorePreviousStep } from 'utils/navigation';
import { DOWNLOAD_REPORT_STEP } from 'constants/report-steps';
import { CONTRACTOR_REPORT_TYPE, EMPLOYEE_REPORT_TYPE } from 'constants/common';

function DownloadReportPage({ report }: { report: Report }): JSX.Element {
  const navigate = useNavigate();
  const [reportResult, setReportResult] = useState<Array<Record<string, string>>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [enableCreatingContractorReport, setEnableCreatingContractorReport] =
    useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    void (async () => {
      setReportResult((await getReportResult(report?.id)) ?? []);
      if (report.reportType === EMPLOYEE_REPORT_TYPE) {
        const contractorReport = await getContractorReport();
        if (contractorReport === null || contractorReport === undefined) {
          setEnableCreatingContractorReport(true);
        }
      }
      setIsLoading(false);
    })();
  }, []);

  const handleGoBackClick = (): void => {
    void navigateAndStorePreviousStep(report, DOWNLOAD_REPORT_STEP, navigate);
  };

  const handleDownload = (): void => {
    void (async () => {
      setProcessing(true);
      const reportFile = await getReportResultCSV(report?.id);
      if (reportFile != null) {
        setProcessing(false);
        setDownloadStarted(true);
        downloadCSV(await reportFile.text(), reportResultFileName(report));
      }
    })();
  };

  return (
    <>
      <DownloadStartedModal
        report={report}
        open={downloadStarted}
        setOpen={setDownloadStarted}
        enableCreatingContractorReport={enableCreatingContractorReport}
      />
      <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
        <a onClick={handleGoBackClick} className="btn-secondary cursor-pointer mb-8">
          <CaretLeft size={18} className="mr-2" /> Back
        </a>
        <div className="font-extrabold text-2xl mx-auto mb-2">
          Preview and download your&nbsp;
          {report.reportType === CONTRACTOR_REPORT_TYPE && 'labor contractor'} report
        </div>
        <div className="flex justify-between mb-10">
          <div className="text-base w-3/4">
            Your {report.reportType === CONTRACTOR_REPORT_TYPE && 'labor contractor'} report is
            ready to be downloaded. After downloading, follow the step-by-step instructions on the
            following screen to easily file your report.
          </div>
          <div>
            <a onClick={handleDownload} className="btn-primary cursor-pointer">
              <DownloadSimple size={18} className="mr-1" />
              &nbsp;Download
              {processing && <Spinner className="ml-2" />}
            </a>
          </div>
        </div>
        {isLoading ? (
          <div className="text-center">
            <Spinner className="text-gray-500" size={48} />
          </div>
        ) : (
          <div className="overflow-x-scroll">
            <ReportResult reportResultData={reportResult} reportType={report?.reportType} />
          </div>
        )}
      </div>
    </>
  );
}

export default withReport(DownloadReportPage);
